import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { flatMap, map, publishReplay, refCount } from 'rxjs/operators';

import { EstablishmentService } from '../../shared/establishment/establishment.service';
import { RpcService } from '../../shared/http/rpc.service';
import { Role, Roles } from './role';
import { User, UserDTO } from './user';
import { TokenService } from '../../authentication/token.service';

@Injectable()
export class UsersService {
  private _user$: Observable<User>;
  private _userChanged: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private userSubject: BehaviorSubject<User> = new BehaviorSubject(null);

  constructor(
    private rpcService: RpcService,
    private tokenService: TokenService,
    private establishmentService: EstablishmentService
  ) {
    this.tokenService.tokenChanged$.subscribe(() => {
      this._user$ = null;
    });

    this.establishmentService.establishmentChanged$.subscribe(
      (isChanged: boolean) => {
        if (isChanged) {
          this.getUser(true).subscribe();
        }
      }
    );
  }

  public resetPasswordFromEmail(
    emailToken: string,
    password: string
  ): Observable<any> {
    const parameters: [string, string, string] = [
      emailToken,
      password,
      password
    ];
    // no token
    return this.rpcService.makeGenericCall('manager.resetPassword', parameters);
  }

  public sendEmailToResetPassword(username: string): Observable<any> {
    // no token
    return this.rpcService.makeGenericCall('manager.resetPassword', [username]);
  }

  public resetPassword(
    userId: number,
    oldPassword: string,
    newPassword: string
  ): Observable<any> {
    const parameters: [number, string, string] = [
      userId,
      oldPassword,
      newPassword
    ];
    return this.rpcService.makeManagerCall('updateUserPassword', parameters);
  }

  public get user(): Observable<User> {
    return this.userSubject.asObservable();
  }

  // TODO: HOS-10017 - don't use mix of Observables and BehaviourSubjects
  public getUser(forceReload: boolean = false): Observable<User> {
    return this.fetchUser(forceReload).pipe(
      map((user: User) => {
        this.userSubject.next(user);
        this._userChanged.next(true);
        return user;
      })
    );
  }

  public allowProducts(): Observable<boolean> {
    return this.user$.pipe(
      map((user: User) => {
        return user.roles.some(
          (role: Role) =>
            (role.name === Roles.ALLOW_PRODUCT_ACCESS ||
              role.name ===
                Roles.BACKWARDS_COMPATIBILITY_ALLOW_PRODUCT_ACCESS) &&
            role.checked
        );
      })
    );
  }

  public allowReports(): Observable<boolean> {
    return this.user$.pipe(
      map((user: User) => {
        return user.roles.some(
          (role: Role) =>
            (role.name === Roles.ALLOW_REPORTING_ACCESS ||
              role.name ===
                Roles.BACKWARDS_COMPATIBILITY_ALLOW_REPORTING_ACCESS) &&
            role.checked
        );
      })
    );
  }

  private fetchUser(forceReload: boolean = false): Observable<User> {
    if (!this.user$ || forceReload) {
      this.user$ = this.rpcService.makeManagerCall('getUser').pipe(
        map((user: UserDTO) => {
          return User.fromDTO(user);
        }),
        flatMap((user: User) => {
          return this.fetchUserWithRoles(user);
        }),
        publishReplay(1),
        refCount()
      );
    }
    return this.user$;
  }

  private fetchUserWithRoles(user: User): Observable<User> {
    return this.rpcService.makeManagerCall('getRolesOfUser', [user.id]).pipe(
      map((roles: [Object]) => {
        if (Array.isArray(roles)) {
          user.roles = roles.map((role: any) => {
            return new Role(
              role.name,
              role.checked,
              role.deleted,
              role.info,
              role.oid
            );
          });
        } else {
          user.roles = [];
        }
        return user;
      }),
      publishReplay(1),
      refCount()
    );
  }

  public get user$(): Observable<User> {
    return this._user$;
  }

  public set user$(value: Observable<User>) {
    this._user$ = value;
  }
}
